/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import { FrameworkCatalog } from '@app/app/shared/services/frameworks/framework-catalog';
import { StyleguideModule } from '@app/app/shared/styleguide/styleguide.module';
import { LucideFolderOpen, LucideFolders, LucideLayoutGrid, LucideSquarePen, LucideTrash2 } from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { FormEvidenceConfig, evidenceKindKey } from './form-evidence-config';

/**
 * An evidence block as the author sees it in the editor: what it asks the learner for, at a glance. Also the static
 * rendering wherever a section is shown without its learner state (the editor's preview, a section not yet started).
 */
@Component({
  selector: 'ptl-form-evidence-editor',
  standalone: true,
  imports: [TranslocoModule, StyleguideModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-evidence-editor.component.html',
  styleUrls: ['./form-evidence-editor.component.scss'],
})
export class FormEvidenceEditorComponent {
  readonly config = input.required<FormEvidenceConfig>();
  readonly readOnly = input(false);

  readonly configure = output<void>();
  readonly remove = output<void>();

  protected readonly LucideSquarePen = LucideSquarePen;
  protected readonly LucideTrash2 = LucideTrash2;

  private readonly catalog = inject(FrameworkCatalog);

  readonly icon = computed(() => {
    switch (this.config().kind) {
      case 'OPEN_EVIDENCE':
        return LucideFolders;
      case 'FRAMEWORK_EVIDENCE':
        return LucideLayoutGrid;
      default:
        return LucideFolderOpen;
    }
  });
  readonly typeKey = computed(() => `translations.v3Forms.evidence.${evidenceKindKey(this.config().kind)}.typeLabel`);

  /**
   * The framework the block's tags come from, by name once the organisation's frameworks have loaded (the drawer loads
   * them); null when it has none.
   */
  readonly frameworkName = computed(() => {
    const config = this.config();
    // A slot's framework only means something once a tag from it is picked.
    const uid = config.kind === 'FRAMEWORK_EVIDENCE' || config.tagId ? config.frameworkUid : null;
    return uid ? (this.catalog.list().find((framework) => framework.uid === uid)?.title ?? null) : null;
  });

  /** A framework block's domains, by name, for the author to see the grid at a glance. */
  readonly domainNames = computed(() =>
    this.config().kind === 'FRAMEWORK_EVIDENCE'
      ? this.config()
          .domains.map((domain) => domain.name)
          .filter((name) => !!name)
      : [],
  );

  constructor() {
    effect(() => {
      // Authors can read the organisation's frameworks; the read-only card names the framework only if it's loaded.
      if (this.config().frameworkUid && !this.readOnly()) {
        this.catalog.loadList();
      }
    });
  }

  /** "1–5 items", "At least 2 items", "Up to 3 items"; for an open block, groups and items per group. */
  readonly limits = computed<{ key: string; params: Record<string, number> }[]>(() => {
    const config = this.config();
    if (config.kind === 'FRAMEWORK_EVIDENCE') {
      const required = config.required ? config.domains.filter((domain) => domain.required).length : 0;
      return [
        { key: 'translations.v3Forms.evidence.limits.domains', params: { count: config.domains.length } },
        ...(required ? [{ key: 'translations.v3Forms.evidence.limits.domainsRequired', params: { count: required } }] : []),
      ];
    }
    if (config.kind === 'OPEN_EVIDENCE') {
      return [
        rangeKey('groups', config.required ? config.minGroups : 0, config.maxGroups),
        ...(config.required && config.minItemsPerGroup > 0
          ? [{ key: 'translations.v3Forms.evidence.limits.perGroup', params: { min: config.minItemsPerGroup } }]
          : []),
      ];
    }
    return [rangeKey('items', config.required ? config.minItems : 0, config.maxItems)];
  });
}

function rangeKey(noun: 'items' | 'groups', min: number, max: number | null): { key: string; params: Record<string, number> } {
  const base = `translations.v3Forms.evidence.limits.${noun}`;
  if (max !== null && min > 0) {
    return { key: `${base}.range`, params: { min, max } };
  }
  if (max !== null) {
    return { key: `${base}.upTo`, params: { max } };
  }
  if (min > 0) {
    return { key: `${base}.atLeast`, params: { min } };
  }
  return { key: `${base}.any`, params: {} };
}
